import type { ChangeEvent, InputHTMLAttributes } from 'react'
import { forwardRef, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../../ui/Icon'
import { hasNativeDatePicker, pickNativeDate } from '../../../app/nativeBridge'

/** Нажатие и клавиши поле обрабатывает само. */
export type DateInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onClick' | 'onKeyDown'
>

/**
 * Записать значение так, будто его выбрали в поле: React и react-hook-form
 * получат обычный onChange. Значение ставим сеттером прототипа, иначе React не
 * заметит, что оно сменилось.
 */
function typeValue(input: HTMLInputElement, value: string) {
  Reflect.set(HTMLInputElement.prototype, 'value', value, input)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * Поле даты с родным выбором телефона. `placeholder` у даты WebKit не
 * показывает — пустое поле было бы просто белым, поэтому подсказку «дд.мм.гггг»
 * рисуем сами поверх, пока дата не выбрана, а родной текст Chrome и Android
 * («mm/dd/yyyy») прячем, чтобы не было двух подсказок. Нажатие проходит сквозь
 * подсказку в поле.
 * Во Flutter-приложении дату выбирают в окне оболочки (`datePicker.open`), а
 * поле только показывает её. Стереть выбранную дату — кнопкой «×»: в окнах
 * выбора кнопки «Очистить» нет.
 * Работает и управляемым (`value`), и с react-hook-form (`register`).
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { onChange, value, defaultValue, min, max, disabled, readOnly, ...props },
  ref,
) {
  const { t } = useTranslation('common')
  const field = useRef<HTMLInputElement | null>(null)
  const opening = useRef(false)
  const [filled, setFilled] = useState(Boolean(value ?? defaultValue))
  // Старая оболочка ответила не тем: перерисовать поле уже с выбором браузера.
  const [, setShellMissing] = useState(false)
  const empty = value === undefined ? !filled : !value
  const editable = !disabled && !readOnly
  const inShell = editable && hasNativeDatePicker()

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      field.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  async function openShellPicker(input: HTMLInputElement) {
    if (opening.current) return
    opening.current = true
    // Пустые границы мост не отправляет.
    const minDate = String(min ?? '')
    const maxDate = String(max ?? '')
    // Без начальной даты окно встало бы на сегодня, а это позже `max`.
    const picked = await pickNativeDate({ initialDate: input.value || maxDate, minDate, maxDate })
    opening.current = false
    if (picked === undefined) {
      setShellMissing(true)
      input.readOnly = false
      try {
        input.showPicker()
      } catch {
        // Нажатие уже «остыло» — выбор браузера откроется следующим нажатием.
      }
    } else if (picked !== null) {
      typeValue(input, picked)
    }
  }

  return (
    <span className={empty ? 'date-input date-input--empty' : 'date-input'}>
      <input
        ref={setRef}
        type="date"
        value={value}
        defaultValue={defaultValue}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly || inShell}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setFilled(event.target.value !== '')
          onChange?.(event)
        }}
        onClick={(event) => {
          if (!inShell) return
          event.preventDefault()
          void openShellPicker(event.currentTarget)
        }}
        onKeyDown={(event) => {
          if (!inShell || (event.key !== 'Enter' && event.key !== ' ')) return
          event.preventDefault()
          void openShellPicker(event.currentTarget)
        }}
        {...props}
      />
      {empty ? (
        <span className="date-input__ph" aria-hidden="true">
          {t('datePlaceholder')}
        </span>
      ) : editable ? (
        <Press
          squish="icon"
          className="date-input__clear"
          aria-label={t('dateClear')}
          onPress={() => {
            typeValue(field.current!, '')
          }}
        >
          <Icon name="i-x" size={16} />
        </Press>
      ) : null}
    </span>
  )
})
